import React, { useState } from 'react';
import { TextField, Button, Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { adjustInputYear, formatDate } from '../../_utils/utils';
import { useSelector } from 'react-redux';
import { RootState } from '../../stores/store';
import { VERSION } from '../../_constants/apiConst';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import { DATE_FORMAT } from '../../_constants/Const';
import dayjs, { Dayjs } from 'dayjs';
import { PostRepairRequest } from '../../services/apiTypes';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

const styleButton = {
  width: '50%',
  height: '5rem',
  mx: 2,
  my: 3,
};

type StartInspectionDataType = {
  rmaNo: string;
  serialNo: string;
  model: string;
  customerName: string;
  scopeConnectionCount: number;
  userId: string;
  userName: string;
  ownership: string;
  installDate: string;
  originalCustomerName: string;
  customerComment: string;
  warrantyEndDate: string;
  contract: string;
  distributor: string;
  customerPriority: string;
  exfmStartUrl: string;
  exfmApiUrl: string;
  exfmStorageAccountUrl: string;
  exfmAreaInfo: number;
};

const defaultRequestData: StartInspectionDataType = {
  rmaNo: 'TEST',
  serialNo: '3C729G047',
  model: 'EC-760R-V/M',
  customerName: 'NEW HYDE PARK ENDOSCOPY LLC',
  scopeConnectionCount: 2063,
  userId: '17110155',
  userName: 'test user',
  ownership: 'Demo',
  installDate: '2025-07-22T09:00:00.000Z',
  originalCustomerName: 'test',
  customerComment: 'rotating wheel stiffness (repeated malfunction)',
  warrantyEndDate: '2025-08-22T09:00:00.000Z',
  contract: '2019100_Y5',
  distributor: 'XXXXXX',
  customerPriority: 'VIP',
  exfmStartUrl: 'https://exfm-us-exfm-us-test906-webapp01.azurewebsites.net',
  exfmApiUrl:
    'https://exfm-us-exfm-global-api-test906-webapp01.azurewebsites.net',
  exfmStorageAccountUrl:
    'https://exfmustest906blob04.blob.core.windows.net/exfm-files-us/print_images/',
  exfmAreaInfo: 0,
};

const ExFM: React.FC = () => {
  const { t } = useTranslation();
  const { userData } = useSelector((state: RootState) => state.userInfo);

  const [repairRequest, setRepairRequest] =
    useState<StartInspectionDataType>(defaultRequestData);

  const reset = () => {
    setRepairRequest(defaultRequestData);
  };

  const startInspection = async () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/${VERSION}/start-inspection`;

    const CloneRepairRequest = {
      ...repairRequest,
      exfmStartUrl: encodeURIComponent(repairRequest.exfmStartUrl),
      exfmApiUrl: encodeURIComponent(repairRequest.exfmApiUrl),
      exfmStorageAccountUrl: encodeURIComponent(
        repairRequest.exfmStorageAccountUrl,
      ),
    };

    const header = {
      method: 'POST',
      body: JSON.stringify(CloneRepairRequest),
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const response = await fetch(url, header);
    const data = await response.json();

    // InspeX起動
    window.open(data.startUrl);

    return response;
  };

  const startQc = async () => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/${VERSION}/start-qc`;
    const header = {
      method: 'POST',
      body: JSON.stringify({
        rmaNo: repairRequest.rmaNo,
        userId: 'test11111',
        userName: 'testQcUser11',
      }),
      headers: {
        'Content-Type': 'application/json',
      },
    };

    const response = await fetch(url, header);
    const data = await response.json();

    // InspeX起動
    window.open(data.startUrl);

    return response;
  };

  const onClickRepair = () => {
    // 帳票を開く
    window.open(`${window.origin}/#/Report?rmano=${repairRequest.rmaNo}`);
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    const fieldName = name as keyof StartInspectionDataType;

    if (fieldName === 'installDate' || fieldName === 'warrantyEndDate') {
      setRepairRequest({
        ...repairRequest,
        [fieldName]: formatDate(value, userData.dateFormat),
      });
    } else if (typeof repairRequest[fieldName] === 'number') {
      if (fieldName === 'scopeConnectionCount') {
        // 正の値のみ入力可、テキスト直入力への対応
        const newValue = Math.max(Number(value), 0);
        setRepairRequest({ ...repairRequest, [fieldName]: Number(newValue) });
      } else if (fieldName === 'exfmAreaInfo') {
        let newValue = Math.max(Number(value), 0);
        // 0～10以外は0とする
        if (newValue < 0 || newValue > 10) {
          newValue = 0;
        }
        setRepairRequest({ ...repairRequest, [fieldName]: newValue });
      } else {
        setRepairRequest({ ...repairRequest, [fieldName]: Number(value) });
      }
    } else {
      setRepairRequest({ ...repairRequest, [fieldName]: value });
    }
  };

  const handleInputDateChange = (
    value: string | number | Dayjs | null,
    name: keyof PostRepairRequest,
  ) => {
    let date = '';
    if (value instanceof Object) {
      // format通りの年月日の形式にする
      const formattedDate =
        value.year().toString() +
        '-' +
        Number(value.month() + 1).toString() +
        '-' +
        value.date().toString();
      const adjustedDate = adjustInputYear(dayjs(formattedDate));
      const formatString =
        userData.dateFormat === 2 // DD-MM-YY → DD-MM-YYYY
          ? DATE_FORMAT[1]
          : DATE_FORMAT[userData.dateFormat];
      date = adjustedDate ? dayjs(adjustedDate).format(formatString) : '';
    }
    setRepairRequest({
      ...repairRequest,
      [name]: date,
    });
  };

  return (
    <Box mt={10} display="flex" flexDirection="column" width="50%" ml="25%">
      <TextField
        autoFocus
        margin="dense"
        label={t('LBL_RMA_NO')}
        type="text"
        fullWidth
        name="rmaNo"
        sx={{ bgcolor: 'warning.light' }}
        value={repairRequest.rmaNo}
        onChange={handleInputChange}
      />
      <TextField
        margin="dense"
        label={t('LBL_SERIAL_NO')}
        type="text"
        fullWidth
        name="serialNo"
        sx={{ bgcolor: 'warning.light' }}
        value={repairRequest.serialNo}
        onChange={handleInputChange}
      />
      <TextField
        margin="dense"
        label={t('LBL_MODEL')}
        type="text"
        fullWidth
        name="model"
        sx={{ bgcolor: 'warning.light' }}
        value={repairRequest.model}
        onChange={handleInputChange}
      />
      <TextField
        margin="dense"
        label={t('LBL_SCOPE_CONNECTION_COUNT')}
        type="number"
        fullWidth
        name="scopeConnectionCount"
        value={repairRequest.scopeConnectionCount ?? ''}
        onChange={handleInputChange}
        slotProps={{ htmlInput: { min: 0 } }} // 正の値のみ入力可
      />
      <TextField
        margin="dense"
        label={t('LBL_INSPECTOR_ID')}
        type="text"
        fullWidth
        name="userId"
        value={repairRequest.userId}
        onChange={handleInputChange}
      />
      <TextField
        margin="dense"
        label={t('LBL_INSPECTOR_NAME')}
        type="text"
        fullWidth
        name="userName"
        value={repairRequest.userName}
        onChange={handleInputChange}
      />
      <TextField
        margin="dense"
        label={t('LBL_OWNERSHIP')}
        type="text"
        fullWidth
        name="ownership"
        value={repairRequest.ownership}
        onChange={handleInputChange}
      />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label={t('LBL_INSTALL_DATE')}
          value={
            repairRequest.installDate === ''
              ? null
              : dayjs(repairRequest.installDate)
          }
          onChange={(value) => handleInputDateChange(value, 'installDate')}
          slotProps={{
            textField: {
              fullWidth: true,
              margin: 'dense',
            },
            field: {
              clearable: true,
              onClear: () => handleInputDateChange('', 'installDate'),
            },
          }}
          format={DATE_FORMAT[userData.dateFormat]}
          views={['year', 'month', 'day']}
        />
      </LocalizationProvider>
      <TextField
        margin="dense"
        label={t('LBL_CUSTOMER')}
        type="text"
        fullWidth
        name="customerName"
        value={repairRequest.customerName}
        onChange={handleInputChange}
      />
      <TextField
        margin="dense"
        label={t('LBL_ORIGINAL_CUSTOMER')}
        type="text"
        fullWidth
        name="originalCustomerName"
        value={repairRequest.originalCustomerName}
        onChange={handleInputChange}
      />
      <TextField
        margin="dense"
        label={t('LBL_CUSTOMER_PRIORITY')}
        type="text"
        fullWidth
        name="customerPriority"
        value={repairRequest.customerPriority}
        slotProps={{
          htmlInput: {
            maxLength: 20,
          },
        }}
        onChange={handleInputChange}
      />
      <TextField
        margin="dense"
        label={t('LBL_DISTRIBUTOR')}
        type="text"
        fullWidth
        name="distributor"
        value={repairRequest.distributor}
        onChange={handleInputChange}
      />
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          label={t('LBL_WARRANTY_END_DATE')}
          value={
            repairRequest.warrantyEndDate === ''
              ? null
              : dayjs(repairRequest.warrantyEndDate)
          }
          onChange={(value) => handleInputDateChange(value, 'warrantyEndDate')}
          slotProps={{
            textField: {
              fullWidth: true,
              margin: 'dense',
            },
            field: {
              clearable: true,
              onClear: () => handleInputDateChange('', 'warrantyEndDate'),
            },
          }}
          format={DATE_FORMAT[userData.dateFormat]}
          views={['year', 'month', 'day']}
        />
      </LocalizationProvider>
      <TextField
        margin="dense"
        label={t('LBL_CONTRACT_NUMBER')}
        type="text"
        fullWidth
        name="contract"
        value={repairRequest.contract}
        onChange={handleInputChange}
      />
      <TextField
        margin="dense"
        label={t('LBL_CUSTOMER_COMMENT')}
        type="text"
        fullWidth
        multiline
        name="customerComment"
        value={repairRequest.customerComment}
        onChange={handleInputChange}
      />
      <TextField
        margin="dense"
        label={'ExFM：遷移先URL'}
        type="text"
        fullWidth
        multiline
        name="exfmStartUrl"
        value={repairRequest.exfmStartUrl}
        onChange={handleInputChange}
      />
      <TextField
        margin="dense"
        label={'ExFM：API（URL）'}
        type="text"
        fullWidth
        multiline
        name="exfmApiUrl"
        value={repairRequest.exfmApiUrl}
        onChange={handleInputChange}
      />
      <TextField
        margin="dense"
        label={'ExFM：ストレージアカウント（URL）'}
        type="text"
        fullWidth
        multiline
        name="exfmStorageAccountUrl"
        value={repairRequest.exfmStorageAccountUrl}
        onChange={handleInputChange}
      />
      <TextField
        margin="dense"
        label={'ExFM：地域情報'}
        type="number"
        fullWidth
        name="exfmAreaInfo"
        value={repairRequest.exfmAreaInfo}
        onChange={handleInputChange}
        slotProps={{ htmlInput: { min: 0 } }} // 正の値のみ入力可
      />
      <Box display="flex">
        <Button
          sx={{ ...styleButton, bgcolor: 'red' }}
          variant="contained"
          onClick={reset}
        >
          Reset
        </Button>
        <Button
          sx={{ ...styleButton, bgcolor: '#01916D' }}
          variant="contained"
          onClick={startInspection}
        >
          Start Inspection
        </Button>
        <Button
          sx={{ ...styleButton, bgcolor: '#1E83BE' }}
          variant="contained"
          onClick={startQc}
          color="primary"
        >
          Start QC
        </Button>
        <Button
          sx={{ ...styleButton, bgcolor: '#EE7736' }}
          variant="contained"
          onClick={onClickRepair}
          color="primary"
        >
          Report
        </Button>
      </Box>
    </Box>
  );
};

export default ExFM;
